import { Typography, Space } from 'antd';
import { snakeCase } from 'lodash';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Field } from 'api/tasks';
import { Tag } from 'components/atoms/tag';
import {
  IntegrationFieldType,
  filterableFieldType,
} from 'components/molecules/integrations-commons';
import { format } from 'components/organisms/tasks/criteria/date/format';
import { FieldType } from 'components/organisms/tasks/fields';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { BooleanFilterOperationType } from 'components/organisms/tasks/filters/boolean';
import { DateFieldFilter } from 'components/organisms/tasks/filters/date';
import { NumberFilterOperationType } from 'components/organisms/tasks/filters/number';
import { SelectFilterOperationType } from 'components/organisms/tasks/filters/select';
import { TextFieldFilterOperationType } from 'components/organisms/tasks/filters/text';
import {
  booleanTriggerOptionRenderers,
  numberTriggerOptionRenderers,
  textTriggerOptionRenderers,
  selectTriggerOptionRenderers,
  dateTriggerOptionRenderers,
} from '../custom-trigger-options';

type ValueRendererProps = {
  selected: FieldFilter;
  customOptionTrigger?: boolean;
};

const { Text } = Typography;

export const ValueRenderer = ({
  selected,
  customOptionTrigger = false,
}: ValueRendererProps) => {
  const { field } = selected;

  // TODO: @krishna or @mohmmed This is because FieldBase dosent seem to have the below on types, but in actual it contains. Its better to extend FieldBase from Field.
  const selectedField = {
    fieldType: (field as unknown as Field).fieldType as FieldType,
    integrationFieldId: (field as unknown as Field).fieldId.toString(),
    fieldDataType: (field as unknown as Field).fieldDataType,
    optionsAvailable: (field as unknown as Field).optionsAvailable,
  };

  switch (filterableFieldType(selectedField)) {
    case IntegrationFieldType.SingleSelect:
    case IntegrationFieldType.MultiSelect: {
      if (
        [
          SelectFilterOperationType.IsOneOf,
          SelectFilterOperationType.IsNoneOf,
        ].includes(selected.type as SelectFilterOperationType)
      ) {
        return (
          <Space>
            <Text>
              {customOptionTrigger ? (
                selectTriggerOptionRenderers(
                  selected.type as SelectFilterOperationType,
                )
              ) : (
                <FormattedMessage
                  id={`task_list.filters.${snakeCase(
                    SelectFilterOperationType[selected.type],
                  )}`}
                />
              )}
            </Text>
            {(selected as any)?.value?.map((each) => (
              <Tag color="cyan">{each}</Tag>
            ))}
          </Space>
        );
      }

      return (
        <Space>
          <Text>
            {customOptionTrigger ? (
              selectTriggerOptionRenderers(
                selected.type as SelectFilterOperationType,
              )
            ) : (
              <FormattedMessage
                id={`task_list.filters.${snakeCase(
                  SelectFilterOperationType[selected.type],
                )}`}
              />
            )}
          </Text>
          {[
            SelectFilterOperationType.IsEmpty,
            SelectFilterOperationType.IsNotEmpty,
          ].includes(selected.type as SelectFilterOperationType) ? null : (
            <Tag color="cyan">{(selected as any)?.value}</Tag>
          )}
        </Space>
      );
    }

    case IntegrationFieldType.Date: {
      return format(selected as DateFieldFilter, dateTriggerOptionRenderers);
    }

    case IntegrationFieldType.TextArea:
    case IntegrationFieldType.Text: {
      return (
        <Space>
          <Text>
            {customOptionTrigger ? (
              textTriggerOptionRenderers(
                selected.type as TextFieldFilterOperationType,
              )
            ) : (
              <FormattedMessage
                id={`settings.integrations.sf.trigger.option.${snakeCase(
                  TextFieldFilterOperationType[selected.type],
                )}`}
              />
            )}
          </Text>
          {[
            TextFieldFilterOperationType.IsEmpty,
            TextFieldFilterOperationType.IsNotEmpty,
          ].includes(selected.type as TextFieldFilterOperationType) ? null : (
            <Text strong>{(selected as any)?.value}</Text>
          )}
        </Space>
      );
    }

    case IntegrationFieldType.Number: {
      return (
        <Space>
          <Text>
            {customOptionTrigger ? (
              numberTriggerOptionRenderers(
                selected.type as NumberFilterOperationType,
              )
            ) : (
              <FormattedMessage
                id={`settings.integrations.sf.trigger.option.${snakeCase(
                  NumberFilterOperationType[selected.type],
                )}`}
              />
            )}
          </Text>
          {[
            NumberFilterOperationType.IsEmpty,
            NumberFilterOperationType.IsNotEmpty,
          ].includes(selected.type as NumberFilterOperationType) ? null : (
            <Text strong>{(selected as any)?.value}</Text>
          )}
        </Space>
      );
    }

    case IntegrationFieldType.Boolean: {
      return (
        <Space>
          <Text>
            {customOptionTrigger ? (
              booleanTriggerOptionRenderers(
                selected.type as BooleanFilterOperationType,
              )
            ) : (
              <FormattedMessage
                id={`settings.integrations.sf.trigger.option.${snakeCase(
                  BooleanFilterOperationType[selected.type],
                )}`}
              />
            )}
          </Text>
          {[
            BooleanFilterOperationType.IsEmpty,
            BooleanFilterOperationType.IsNotEmpty,
          ].includes(selected.type as BooleanFilterOperationType) ? null : (
            <Text strong>{(selected as any)?.value}</Text>
          )}
        </Space>
      );
    }
    default: {
      return <Text strong>{(selected as any)?.value}</Text>;
    }
  }
};
